/* =========================================================================
 * /contact-sales — dedicated lead-capture page for researchers / institutions.
 *
 * Richer than the inline InquiryForm on /researchers: collects name +
 * institution + usage + phone so the sales handoff has enough context to
 * respond personally. Submissions land in Firestore at
 * `contactSales/{auto-id}`. A future Cloud Function (TODO: SendGrid) will
 * notify sales@bripplex.com when a new doc lands.
 *
 * UX: form → on success the form is replaced by a confirmation card
 * promising contact within 24 hours.
 * ========================================================================= */

import { useState } from "react";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import SiteFooter from "components/SiteFooter";

import consumerRoutes from "consumerRoutes";
import footerRoutes from "footer.routes";

import { db, firebase } from "configuration/firebase";

export const ContactSales = () => {
  const [name, setName] = useState("");
  const [institution, setInstitution] = useState("");
  const [usage, setUsage] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return setError("Please enter your name.");
    if (!institution.trim()) return setError("Please enter your institution or organization.");
    if (!email.includes("@")) return setError("Please enter a valid email so we can follow up.");
    if (!usage.trim()) return setError("Tell us a bit about how you plan to use the data.");

    setSubmitting(true);
    try {
      await db.collection("contactSales").add({
        name: name.trim(),
        institution: institution.trim(),
        usage: usage.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        err?.message ||
          "We couldn't send your message — please try again, or email sales@bripplex.com directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DefaultNavbar routes={consumerRoutes} sticky />

      {/* Hero */}
      <MKBox
        component="section"
        sx={({ palette }) => ({
          background: `linear-gradient(180deg, ${palette.white.main} 0%, #EFF6FF 100%)`,
          pt: { xs: 12, md: 16 },
          pb: { xs: 6, md: 8 },
        })}
      >
        <Container>
          <Grid container justifyContent="center" textAlign="center">
            <Grid item xs={12} md={10} lg={9}>
              <MKTypography
                variant="overline"
                color="primary"
                fontWeight="bold"
                sx={{ letterSpacing: "0.1em" }}
              >
                Talk to sales
              </MKTypography>
              <MKTypography
                variant="h1"
                color="dark"
                mt={1.5}
                sx={({ breakpoints, typography: { size } }) => ({
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  [breakpoints.down("md")]: { fontSize: size["3xl"] },
                })}
              >
                Tell us about your project
              </MKTypography>
              <MKTypography
                variant="body1"
                color="text"
                mt={3}
                sx={{ maxWidth: 640, mx: "auto", fontSize: { xs: 16, md: 18 }, lineHeight: 1.6 }}
              >
                Share a few details and our team will reach out within 24 hours to discuss your
                research needs, coverage in your area, and dataset options.
              </MKTypography>
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      {/* Form / confirmation */}
      <MKBox component="section" py={{ xs: 6, md: 10 }} px={{ xs: 2, md: 4 }}>
        <Container maxWidth="sm">
          {submitted ? (
            <MKBox
              sx={({ palette, borders }) => ({
                p: { xs: 4, md: 5 },
                textAlign: "center",
                borderRadius: borders.borderRadius.lg,
                border: `1px solid ${palette.secondary.main}`,
                background: "rgba(20, 184, 166, 0.06)",
              })}
            >
              <MKTypography variant="h4" color="dark" fontWeight="bold">
                Thanks — your message is on its way.
              </MKTypography>
              <MKTypography
                variant="body1"
                color="text"
                mt={2}
                sx={{ maxWidth: 480, mx: "auto", lineHeight: 1.6 }}
              >
                Someone from the Bripplex team will be in touch within 24 hours at{" "}
                <MKBox component="strong" sx={{ color: "dark.main" }}>
                  {email}
                </MKBox>
                .
              </MKTypography>
              <MKButton
                variant="outlined"
                color="primary"
                size="large"
                href="/"
                sx={{ mt: 4, px: 4 }}
              >
                Back to home
              </MKButton>
            </MKBox>
          ) : (
            <MKBox component="form" onSubmit={onSubmit} noValidate>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Your name"
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={submitting}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Institution / organization"
                    variant="outlined"
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                    disabled={submitting}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    label="Phone (optional)"
                    variant="outlined"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={submitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={4}
                    label="How do you plan to use the data?"
                    placeholder="A short description helps us scope the right slice — sensors, geography, time window, end-application."
                    variant="outlined"
                    value={usage}
                    onChange={(e) => setUsage(e.target.value)}
                    disabled={submitting}
                  />
                </Grid>
                {error && (
                  <Grid item xs={12}>
                    <MKTypography variant="body2" color="error">
                      {error}
                    </MKTypography>
                  </Grid>
                )}
                <Grid item xs={12} sx={{ textAlign: "center", mt: 2 }}>
                  <MKButton
                    type="submit"
                    variant="gradient"
                    color="primary"
                    size="large"
                    disabled={submitting}
                    sx={{ px: 5 }}
                  >
                    {submitting ? "Sending…" : "Contact sales"}
                  </MKButton>
                  <MKTypography
                    variant="caption"
                    color="text"
                    sx={{ display: "block", mt: 2, opacity: 0.7 }}
                  >
                    We&apos;ll respond within 24 hours.
                  </MKTypography>
                </Grid>
              </Grid>
            </MKBox>
          )}
        </Container>
      </MKBox>

      <SiteFooter content={footerRoutes} />
    </>
  );
};
